import { IconWifiOff } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  getApiConnectionIssue,
  subscribeApiConnectionIssue,
} from "@common/helpers/apiConnectionStatus";
import { __ } from "@common/helpers/i18n";
import { cn } from "@app/lib/utils";

export default function OfflineBanner({ className }: { className?: string }) {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [apiConnectionIssue, setApiConnectionIssue] = useState(() =>
    getApiConnectionIssue(),
  );

  useEffect(() => {
    const updateStatus = () => setIsOnline(navigator.onLine);

    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);
    const unsubscribe = subscribeApiConnectionIssue(setApiConnectionIssue);

    return () => {
      window.removeEventListener("online", updateStatus);
      window.removeEventListener("offline", updateStatus);
      unsubscribe();
    };
  }, []);

  const title = isOnline
    ? apiConnectionIssue?.title
    : __("You are offline");
  const description = isOnline
    ? apiConnectionIssue?.description
    : __(
        "Check your connection. Some actions may not update until you are back online.",
      );

  if (!title || !description) return null;

  return (
    <div className={cn("mx-auto mt-4 w-full max-w-[1440px]", className)}>
      <section
        role="status"
        className="flex min-w-0 items-start gap-3 rounded-lg border border-warning/25 bg-warning/10 px-4 py-3 text-start text-warning shadow-soft"
      >
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-card/70 ring-1 ring-warning/20">
          <IconWifiOff size={18} stroke={2} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-extrabold leading-6 text-foreground">
            {title}
          </p>
          <p className="text-xs font-semibold leading-5 text-muted-foreground">
            {description}
          </p>
        </div>
      </section>
    </div>
  );
}
